import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, CalendarPlus, Power } from 'lucide-react';
import TopMenu from '../../components/TopMenu';
import StatusBadge from '../../components/MockInterview/StatusBadge';
import { useNotification } from '../../notification/NotificationContext';
import {
  adminGetInterviewers,
  adminAddInterviewer,
  adminToggleInterviewerStatus,
  adminAddSlot,
} from '../../services/mockInterviewService';
import '../../components/MockInterview/MockInterview.css';

const AdminInterviewerPage = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const dark = theme === 'dark';

  const [interviewers, setInterviewers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', bio: '', expertise: '' });
  const [savingAdd, setSavingAdd] = useState(false);

  const [showSlot, setShowSlot] = useState(false);
  const [slotForm, setSlotForm] = useState({ interviewerId: '', slotDateTime: '' });
  const [savingSlot, setSavingSlot] = useState(false);

  const load = () =>
    adminGetInterviewers()
      .then((d) => setInterviewers(Array.isArray(d) ? d : []))
      .catch((e) => showNotification(e.message || 'Failed to load interviewers', 'error'));

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = async (id) => {
    try {
      await adminToggleInterviewerStatus(id);
      showNotification('Status updated', 'success');
      await load();
    } catch (e) {
      showNotification(e.message || 'Failed to toggle status', 'error');
    }
  };

  const submitAdd = async () => {
    const email = form.email.trim();
    if (!email || !form.expertise.trim()) {
      showNotification('Email and expertise are required', 'warning');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      showNotification('Enter a valid email address', 'warning');
      return;
    }
    setSavingAdd(true);
    try {
      const res = await adminAddInterviewer({
        email,
        bio: form.bio.trim(),
        expertise: form.expertise.trim(),
      });
      const name = res?.displayName || res?.email || email;
      showNotification(`Interviewer added: ${name}`, 'success');
      setShowAdd(false);
      setForm({ email: '', bio: '', expertise: '' });
      await load();
    } catch (e) {
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('not found') || msg.includes('no user')) {
        showNotification('No registered user found with that email.', 'error');
      } else if (msg.includes('already')) {
        showNotification('This user is already an interviewer.', 'error');
      } else {
        showNotification(e.message || 'Failed to add', 'error');
      }
    } finally {
      setSavingAdd(false);
    }
  };

  const submitSlot = async () => {
    if (!slotForm.interviewerId || !slotForm.slotDateTime) {
      showNotification('Pick an interviewer and a slot', 'warning');
      return;
    }
    setSavingSlot(true);
    try {
      await adminAddSlot({
        interviewerId: Number(slotForm.interviewerId),
        slotDateTime: new Date(slotForm.slotDateTime).toISOString(),
      });
      showNotification('Slot published', 'success');
      setShowSlot(false);
      setSlotForm({ interviewerId: '', slotDateTime: '' });
    } catch (e) {
      showNotification(e.message || 'Failed to add slot', 'error');
    } finally {
      setSavingSlot(false);
    }
  };

  if (loading) {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-loading"><div className="mi-spin" /></div>
      </div>
    );
  }

  const activeCount = interviewers.filter((i) => i.isActive).length;
  const inactiveCount = interviewers.length - activeCount;

  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="mi-ed">

        <div className="mi-ed-strip">
          <div className="mi-ed-strip-l">
            <span className="mi-ed-issue">№ 05 — Backstage / Admin</span>
            <span>{interviewers.length} on the roster</span>
            <span>{activeCount} active</span>
          </div>
          <button className="mi-ed-back" onClick={() => navigate('/mock-interview')}>
            <ArrowLeft size={12} />
            Studio
          </button>
        </div>

        <div className="mi-ed-pagehead">
          <h1>
            Curate the <em>roster</em>.
            <svg
              className="mi-ed-scribble"
              viewBox="0 0 220 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M3 8 C 50 2, 110 11, 165 5 S 210 9, 217 4" />
            </svg>
          </h1>
          <p>
            Onboard new interviewers, toggle who&rsquo;s taking calls this
            week, and publish slots so candidates have something to grab.
          </p>
        </div>

        <div className="mi-ed-meta">
          <div className="mi-ed-meta-item">
            <span className="mi-ed-meta-num">{interviewers.length}</span>
            <span className="mi-ed-meta-lbl">on roster</span>
          </div>
          <div className="mi-ed-meta-item">
            <span className="mi-ed-meta-num">{activeCount}</span>
            <span className="mi-ed-meta-lbl">taking calls</span>
          </div>
          <div className="mi-ed-meta-item">
            <span className="mi-ed-meta-num">{inactiveCount}</span>
            <span className="mi-ed-meta-lbl">on the bench</span>
          </div>
        </div>

        <div className="mi-row mi-row-between" style={{ marginTop: 28, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <div className="mi-ed-colhead" style={{ margin: 0 }}>
            <span>The roster</span>
            <em>everyone you&rsquo;ve invited</em>
          </div>
          <div className="mi-row" style={{ gap: 8 }}>
            <button className="mi-btn" onClick={() => setShowSlot(true)}>
              <CalendarPlus size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Publish slot
            </button>
            <button className="mi-ed-go" onClick={() => setShowAdd(true)}>
              <Plus size={14} />
              Add interviewer
            </button>
          </div>
        </div>

        {interviewers.length === 0 && (
          <div className="mi-ed-empty-fancy">
            <h4>The <em>roster</em> is empty.</h4>
            <p>Add your first interviewer — they need to already have an account on the platform.</p>
            <button
              className="mi-ed-go"
              style={{ marginTop: 18 }}
              onClick={() => setShowAdd(true)}
            >
              Add interviewer
            </button>
          </div>
        )}

        {interviewers.length > 0 && (
          <div className="mi-ed-list">
            {interviewers.map((i, idx) => {
              const num = String(idx + 1).padStart(2, '0');
              return (
                <div
                  key={i.id}
                  className="mi-ed-row"
                  style={{ gridTemplateColumns: '44px 1.4fr 1.6fr 110px 130px' }}
                >
                  <span
                    className="mi-ed-fbnum"
                    style={{ width: 36, height: 36, fontSize: 14 }}
                  >
                    {num}
                  </span>
                  <div>
                    <span className="mi-ed-row-cell-lbl">Interviewer</span>
                    <span className="mi-ed-row-cell-val">
                      {i.displayName || i.email || i.userId}
                    </span>
                    {i.email && (
                      <div style={{ fontSize: 12, opacity: 0.55, marginTop: 2 }}>{i.email}</div>
                    )}
                    {i.bio && (
                      <div
                        style={{
                          fontFamily: 'Georgia, serif',
                          fontStyle: 'italic',
                          fontSize: 12.5,
                          opacity: 0.6,
                          marginTop: 4,
                          maxWidth: 320,
                        }}
                      >
                        &ldquo;{i.bio}&rdquo;
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="mi-ed-row-cell-lbl">Expertise</span>
                    <span className="mi-ed-row-cell-val">{i.expertise || '—'}</span>
                  </div>
                  <div>
                    <span className="mi-ed-row-cell-lbl">Status</span>
                    <StatusBadge status={i.isActive ? 'Active' : 'Inactive'} />
                  </div>
                  <div style={{ justifySelf: 'end' }}>
                    <button
                      className="mi-btn mi-btn-sm"
                      onClick={() => handleToggle(i.id)}
                      title={i.isActive ? 'Take off the bench' : 'Bring back online'}
                    >
                      <Power size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {i.isActive ? 'Bench' : 'Activate'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="mi-modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="mi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mi-ed-modal-head">
              <h3>Add an <em>interviewer</em></h3>
              <button className="mi-modal-close" onClick={() => setShowAdd(false)}>×</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="mi-ed-label">Gmail address</label>
              <input
                type="email"
                className="mi-ed-input"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="user@gmail.com"
              />
              <p className="mi-ed-help">
                They need to already have an account here — we look them up by email.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="mi-ed-label">Expertise</label>
              <input
                className="mi-ed-input"
                value={form.expertise}
                onChange={(e) => setForm((f) => ({ ...f, expertise: e.target.value }))}
                placeholder="Frontend · System Design"
              />
            </div>

            <div style={{ marginBottom: 22 }}>
              <label className="mi-ed-label">Short bio</label>
              <textarea
                className="mi-ed-textarea"
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                placeholder="One or two sentences candidates will see."
              />
            </div>

            <div className="mi-row mi-row-end" style={{ gap: 10 }}>
              <button className="mi-btn" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="mi-ed-go" onClick={submitAdd} disabled={savingAdd}>
                {savingAdd ? 'Saving…' : 'Add to roster'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSlot && (
        <div className="mi-modal-backdrop" onClick={() => setShowSlot(false)}>
          <div className="mi-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mi-ed-modal-head">
              <h3>Publish a <em>slot</em></h3>
              <button className="mi-modal-close" onClick={() => setShowSlot(false)}>×</button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="mi-ed-label">Interviewer</label>
              <select
                className="mi-ed-select"
                value={slotForm.interviewerId}
                onChange={(e) => setSlotForm((f) => ({ ...f, interviewerId: e.target.value }))}
              >
                <option value="">— Choose someone active —</option>
                {interviewers.filter((i) => i.isActive).map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.expertise} — {i.displayName || i.email || i.userId}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 22 }}>
              <label className="mi-ed-label">Date &amp; time</label>
              <input
                type="datetime-local"
                className="mi-ed-input"
                value={slotForm.slotDateTime}
                onChange={(e) => setSlotForm((f) => ({ ...f, slotDateTime: e.target.value }))}
              />
              <p className="mi-ed-help">Candidates will see this slot show up on the booking page.</p>
            </div>

            <div className="mi-row mi-row-end" style={{ gap: 10 }}>
              <button className="mi-btn" onClick={() => setShowSlot(false)}>Cancel</button>
              <button className="mi-ed-go" onClick={submitSlot} disabled={savingSlot}>
                {savingSlot ? 'Publishing…' : 'Publish slot'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInterviewerPage;
